import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Layout } from '@/components/Layout';
import { PageHeader } from '@/components/PageHeader';
import { toDateString } from '@/lib/format';
import { useTransactions } from '@/hooks/useTransactions';
import { useCategories } from '@/hooks/useCategories';
import { useFilters } from '@/hooks/useFilters';
import { FilterBar } from '@/components/filters';
import { Loader2, Plus } from 'lucide-react';
import { TransactionList } from '@/components/transactions';
import { formatCurrency } from '@/lib/format';

const Transactions: React.FC = () => {
  const navigate = useNavigate();
  const filters = useFilters({ includeTransactionFilters: true });
  const { applied } = filters;

  const [pageSize, setPageSize] = useState(10);
  const [currentPage, setCurrentPage] = useState(1);

  const queryFilters = {
    type: applied.type === 'all' ? undefined : applied.type,
    from: toDateString(applied.from),
    to: toDateString(applied.to),
    category: applied.category === 'all' ? undefined : applied.category,
  };

  const { data: allTransactionsResponse, isLoading: isLoadingAllTransactions } = useTransactions({
    filters: queryFilters,
  });

  const { data: transactionsResponse, isLoading: isLoadingTransactions } = useTransactions({
    filters: {
      ...queryFilters,
      limit: pageSize,
      offset: (currentPage - 1) * pageSize,
    },
  });

  const { data: categories = [], isLoading: isLoadingCategories } = useCategories({});

  const transactions = transactionsResponse?.data ?? [];
  const allTransactions = allTransactionsResponse?.data ?? [];
  const totalItems = transactionsResponse?.total ?? 0;
  const isInitialLoading =
    isLoadingCategories || (isLoadingAllTransactions && !allTransactionsResponse);
  const totalPages = Math.ceil(totalItems / pageSize);

  useEffect(() => {
    setCurrentPage(1);
  }, [applied.type, applied.category, applied.from, applied.to, pageSize]);

  const totalIncome = allTransactions
    .filter((t) => t.type === 'income')
    .reduce((sum, t) => sum + parseFloat(t.amount), 0);

  const totalExpenses = allTransactions
    .filter((t) => t.type === 'expense')
    .reduce((sum, t) => sum + parseFloat(t.amount), 0);

  if (isInitialLoading) {
    return (
      <Layout>
        <div className="flex items-center justify-center min-h-[50vh]">
          <Loader2 className="h-7 w-7 animate-spin text-primary" />
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-6">
        <PageHeader
          title="Transações"
          eyebrow={
            totalItems === 0
              ? 'Nenhum registro no período'
              : totalItems === 1
              ? '1 registro no período'
              : `${totalItems} registros no período`
          }
          action={
            <Link
              to="/transactions/add"
              className="flex items-center gap-1.5 px-3 py-2 rounded-md bg-primary text-primary-foreground text-sm font-semibold hover:opacity-90 transition-opacity"
            >
              <Plus className="h-3.5 w-3.5" />
              Registrar Transações
            </Link>
          }
        />

        <FilterBar
          filters={filters}
          includeTransactionFilters
          categories={categories}
        />

        <section className="grid grid-cols-2 gap-x-10 sm:gap-x-16">
          <div>
            <p className="text-[11px] font-medium uppercase tracking-[0.22em] text-muted-foreground mb-1.5 flex items-center gap-2">
              <span className="inline-block h-1.5 w-1.5 rounded-full bg-income" aria-hidden="true" />
              Total de receitas
            </p>
            <p className="font-display font-bold leading-[1.05] tracking-tight tabular-nums text-foreground text-[clamp(1.4rem,3.2vw,1.875rem)]">
              {formatCurrency(totalIncome)}
            </p>
          </div>
          <div>
            <p className="text-[11px] font-medium uppercase tracking-[0.22em] text-muted-foreground mb-1.5 flex items-center gap-2">
              <span className="inline-block h-1.5 w-1.5 rounded-full bg-expense" aria-hidden="true" />
              Total de despesas
            </p>
            <p className="font-display font-bold leading-[1.05] tracking-tight tabular-nums text-foreground text-[clamp(1.4rem,3.2vw,1.875rem)]">
              {formatCurrency(totalExpenses)}
            </p>
          </div>
        </section>

        <TransactionList
          transactions={transactions}
          categories={categories}
          isLoading={isLoadingTransactions}
          pageSize={pageSize}
          currentPage={currentPage}
          totalItems={totalItems}
          totalPages={totalPages}
          onPageSizeChange={setPageSize}
          onPageChange={setCurrentPage}
          onAddTransaction={() => navigate('/transactions/add')}
        />
      </div>
    </Layout>
  );
};

export default Transactions;
